import { useRef, type ChangeEvent, type FocusEvent, type MouseEvent } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatNumber, normalizeWholeNumberText } from '@/utils/number';
import type { AmountCap } from '../utils/verifiability';

const MESSAGE_LINE_CLASS = 'flex h-3.5 w-full justify-end whitespace-nowrap text-2xs leading-3.5';

interface AmountInputProps {
  id: string;
  label: string;
  value: string;
  cap: AmountCap;
  onChange: (value: string) => void;
  problem: string | undefined;
  disabled?: boolean;
}

export function AmountInput({
  id,
  label,
  value,
  cap,
  onChange,
  problem,
  disabled,
}: AmountInputProps) {
  const isFocusSelectionPendingRef = useRef(false);
  const messageId = `${id}-message`;

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    event.currentTarget.select();
    isFocusSelectionPendingRef.current = true;
  }

  function handleMouseUp(event: MouseEvent<HTMLInputElement>) {
    if (!isFocusSelectionPendingRef.current) return;
    event.preventDefault();
    isFocusSelectionPendingRef.current = false;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const wholeNumberText = normalizeWholeNumberText(event.target.value);
    if (wholeNumberText === undefined) return;
    onChange(wholeNumberText);
  }

  function handleBlur() {
    isFocusSelectionPendingRef.current = false;
    if (value === '') onChange(String(cap.amount));
  }

  return (
    <div className="flex w-28 flex-col items-end gap-0.5">
      <span aria-hidden="true" className={MESSAGE_LINE_CLASS} />
      <Input
        id={id}
        aria-label={label}
        aria-invalid={problem !== undefined}
        aria-describedby={messageId}
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onFocus={handleFocus}
        onMouseUp={handleMouseUp}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn('h-9 w-full text-right tabular-nums', problem && 'border-destructive')}
      />
      <p
        id={messageId}
        className={cn(
          MESSAGE_LINE_CLASS,
          problem ? 'text-destructive' : 'text-muted-foreground tabular-nums',
        )}
      >
        {problem ?? `${cap.kind} ${formatNumber(cap.amount)}`}
      </p>
    </div>
  );
}
